import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../lib/api";
import {
  AUTHORITY_LABEL, DL_CODES, DUTY_STATUSES, EMPLOYMENT_STATUSES, HOLD_CATEGORIES, REQUIREMENT_LABEL,
  RELATIONSHIP_TYPES, detailMessage, humanize,
} from "../lib/driverService";
import { Badge } from "./DriverService";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function DriverServiceProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get(`/driver-service/drivers/${id}`).then((r) => setData(r.data))
      .catch((err) => setError(detailMessage(err, "This driver could not be loaded.")));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600" data-testid="dsp-error">{error}</div>;
  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { profile, summary, capabilities: caps = [] } = data;
  const can = (cap) => caps.includes(cap);

  const act = async (fn, success) => {
    try { await fn(); toast.success(success); load(); } catch (err) { toast.error(detailMessage(err)); }
  };

  return (
    <div className="space-y-5" data-testid="driver-service-profile">
      <Link to="/app/driver-service?tab=drivers" className="text-sm underline text-slate-600">All drivers</Link>
      <div className="rounded-xl border border-slate-200 bg-white p-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold truncate">{profile.display_name}</h1>
          <div className="text-sm text-slate-600">{humanize(profile.relationship_type)} · {profile.has_login ? "Has a login" : "No login (assigned by managers)"}</div>
        </div>
        <Badge status={summary.readiness_now} testId="dsp-readiness" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dsp-states">
        <State label="Driving authority" value={AUTHORITY_LABEL[summary.driving_authority] || humanize(summary.driving_authority)}
               sub={summary.licence_verified_by ? `Verified by ${summary.licence_verified_by}` : "Not verified"} />
        <State label="Employment" value={humanize(profile.employment_status)} />
        <State label="Duty" value={humanize(profile.duty_status)} />
        <State label="Safety" value={humanize(profile.safety?.status)} />
      </div>
      {summary.reason_codes.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="dsp-reasons">
          {summary.reason_codes.map(humanize).join(" · ")}
        </div>
      )}
      {summary.warning_codes.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
          Not blocking driving: {summary.warning_codes.map(humanize).join(" · ")}
        </div>
      )}
      {data.possible_duplicates?.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700">
          Possible duplicate records for a person to review (never merged automatically): {data.possible_duplicates.map((d) => d.display_name).join(", ")}
        </div>
      )}

      {data.legal && <LegalSection profileId={id} rows={data.legal} caps={caps} onChanged={load} />}

      {can("drivers.manage") && (
        <Section title="Relationship and employment">
          <div className="grid sm:grid-cols-2 gap-3">
            <ActionSelect label="Relationship type" options={RELATIONSHIP_TYPES} value={profile.relationship_type}
                          onSubmit={(v) => act(() => api.patch(`/driver-service/drivers/${id}`, { relationship_type: v }), "Saved")} />
            <ActionSelect label="Employment status" options={EMPLOYMENT_STATUSES} value={profile.employment_status} needsReason
                          onSubmit={(v, reason) => act(() => api.post(`/driver-service/drivers/${id}/employment-status`, { status: v, reason }), "Employment status saved")} />
          </div>
          <p className="text-xs text-slate-500 mt-2">Employment status is separate from driving authority and safety. Registration gaps are shown as alerts and never block a legally qualified driver.</p>
        </Section>
      )}

      {can("drivers.availability.manage") && (
        <Section title="Availability">
          <ActionSelect label="Duty status" options={DUTY_STATUSES} value={profile.duty_status}
                        onSubmit={(v) => act(() => api.post(`/driver-service/drivers/${id}/duty-status`, { status: v }), "Duty status saved")} />
          <TimeOff profileId={id} blocks={data.time_off} onChanged={load} />
        </Section>
      )}

      {can("drivers.safety.manage") && (
        <Section title="Safety">
          <p className="text-xs text-slate-500">A safety hold stops dispatch while it is reviewed. It is not a dismissal, suspension of employment or a finding of fault.</p>
          <SafetyHolds profileId={id} safety={profile.safety} onChanged={load} />
        </Section>
      )}

      <Section title="Upcoming trips">
        {data.upcoming_trips.length === 0 ? <p className="text-sm text-slate-500">No upcoming trips.</p> : (
          <ul className="divide-y divide-slate-100 text-sm">
            {data.upcoming_trips.map((t) => (
              <li key={t.id} className="py-2 flex flex-wrap justify-between gap-2">
                <Link to={`/app/bookings/${t.id}`} className="underline">{t.booking_reference}</Link>
                <span className="text-slate-600">{new Date(t.start_datetime).toLocaleString()} · {humanize(t.driver_assignment_status)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function State({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-medium text-sm mt-0.5">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <h2 className="font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function ActionSelect({ label, options, value, onSubmit, needsReason }) {
  const [v, setV] = useState(value);
  const [reason, setReason] = useState("");
  return (
    <div className="text-sm">
      <span className="text-xs text-slate-500">{label}</span>
      <div className="flex flex-wrap gap-2 mt-1">
        <select value={v} onChange={(e) => setV(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 min-w-0">
          {options.map((o) => <option key={o} value={o}>{humanize(o)}</option>)}
        </select>
        {needsReason && <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason"
                               className="px-3 py-2 rounded-lg border border-slate-300 min-w-0 flex-1" />}
        <button onClick={() => onSubmit(v, reason)} disabled={v === value && !needsReason}
                className="px-3 py-2 rounded-lg border border-slate-300 disabled:opacity-50">Save</button>
      </div>
    </div>
  );
}

function LegalSection({ profileId, rows, caps, onChanged }) {
  const canManage = caps.includes("drivers.compliance.manage");
  const [open, setOpen] = useState(null);
  return (
    <Section title="Driver legal readiness">
      <p className="text-xs text-slate-500">“Verified by employer” means your business checked the document. It is not ConfirmPH verification. Licence numbers are never shown here.</p>
      <ul className="divide-y divide-slate-100" data-testid="dsp-legal">
        {rows.filter((r) => r.applicable).map((r) => (
          <li key={r.key} className="py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium">{r.title}</div>
                <div className="text-xs text-slate-500">{REQUIREMENT_LABEL[r.label]} · {humanize(r.status)}
                  {r.verified_by ? ` · verified by ${r.verified_by}` : ""}{r.expiry_date ? ` · valid until ${r.expiry_date}` : ""}</div>
                {r.fields?.dl_codes && <div className="text-xs text-slate-600">DL codes: {[].concat(r.fields.dl_codes).join(", ")}
                  {r.fields.classification ? ` · ${humanize(r.fields.classification)}` : ""}</div>}
              </div>
              {canManage && <button className="text-xs underline" onClick={() => setOpen(open === r.key ? null : r.key)}>Update</button>}
            </div>
            {open === r.key && <LegalForm profileId={profileId} row={r} onDone={() => { setOpen(null); onChanged(); }} />}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function LegalForm({ profileId, row, onDone }) {
  const [file, setFile] = useState(null);
  const [expiry, setExpiry] = useState(row.expiry_date || "");
  const [classification, setClassification] = useState(row.fields?.classification || "PROFESSIONAL");
  const [codes, setCodes] = useState([].concat(row.fields?.dl_codes || []));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const isLicence = row.key === "driver_licence";

  const run = async (verify) => {
    setBusy(true);
    try {
      if (file) {
        await api.post(`/driver-service/drivers/${profileId}/legal/${row.key}/evidence`, {
          data_base64: await fileToBase64(file), content_type: file.type || "application/pdf", filename: file.name,
          document_type: isLicence ? "driver_licence" : "driver_credential", expiry_date: expiry || null });
      }
      if (verify) {
        await api.post(`/driver-service/drivers/${profileId}/legal/${row.key}/employer-verify`, {
          note, expiry_date: expiry || null,
          fields: isLicence ? { classification, dl_codes: codes } : undefined });
        toast.success("Recorded as verified by employer");
      } else {
        toast.success("Document uploaded");
      }
      onDone();
    } catch (err) { toast.error(detailMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 rounded-lg border border-slate-200 p-3 space-y-2 text-sm" data-testid={`dsp-legal-form-${row.key}`}>
      <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <label className="block"><span className="text-xs text-slate-500">Valid until</span>
        <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1 px-3 py-2 rounded-lg border border-slate-300" /></label>
      {isLicence && (
        <>
          <label className="block"><span className="text-xs text-slate-500">Classification</span>
            <select value={classification} onChange={(e) => setClassification(e.target.value)} className="mt-1 px-3 py-2 rounded-lg border border-slate-300">
              {["PROFESSIONAL", "NON_PROFESSIONAL", "STUDENT"].map((c) => <option key={c} value={c}>{humanize(c)}</option>)}
            </select></label>
          <div><span className="text-xs text-slate-500">DL codes on the card</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {DL_CODES.map((c) => (
                <label key={c} className="flex items-center gap-1 text-xs border rounded px-2 py-1">
                  <input type="checkbox" checked={codes.includes(c)}
                         onChange={() => setCodes(codes.includes(c) ? codes.filter((x) => x !== c) : [...codes, c])} /> {c}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
      <label className="block"><span className="text-xs text-slate-500">What you checked (needed to verify)</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300" /></label>
      <div className="flex flex-wrap gap-2">
        <button disabled={busy || !file} onClick={() => run(false)} className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50">Upload only</button>
        <button disabled={busy || note.length < 10} onClick={() => run(true)} className="px-3 py-1.5 rounded-lg btn-brand text-xs disabled:opacity-50">Upload and verify as employer</button>
      </div>
    </div>
  );
}

function TimeOff({ profileId, blocks, onChanged }) {
  const [form, setForm] = useState({ block_type: "day_off", start_at: "", end_at: "" });
  const add = async () => {
    try {
      await api.post(`/driver-service/drivers/${profileId}/time-off`, {
        ...form, start_at: new Date(form.start_at).toISOString(), end_at: new Date(form.end_at).toISOString() });
      toast.success("Time off recorded. Affected trips are flagged for a replacement driver.");
      onChanged();
    } catch (err) { toast.error(detailMessage(err)); }
  };
  return (
    <div className="space-y-2">
      <ul className="text-sm">
        {blocks.map((b) => <li key={b.id}>{humanize(b.block_type)}: {new Date(b.start_at).toLocaleString()} – {new Date(b.end_at).toLocaleString()}</li>)}
      </ul>
      <div className="flex flex-wrap gap-2 text-sm">
        <select value={form.block_type} onChange={(e) => setForm({ ...form, block_type: e.target.value })} className="px-2 py-1.5 rounded border">
          {["day_off", "vacation", "sick_leave", "personal_leave", "unavailable", "available"].map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
        </select>
        <input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="px-2 py-1.5 rounded border min-w-0" />
        <input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="px-2 py-1.5 rounded border min-w-0" />
        <button disabled={!form.start_at || !form.end_at} onClick={add} className="px-3 py-1.5 rounded border disabled:opacity-50">Add</button>
      </div>
    </div>
  );
}

function SafetyHolds({ profileId, safety, onChanged }) {
  const [category, setCategory] = useState("other");
  const [reason, setReason] = useState("");
  const holds = (safety?.holds || []).filter((h) => h.active);
  const open = async () => {
    try {
      await api.post(`/driver-service/drivers/${profileId}/safety-holds`, { category, reason });
      toast.success("Safety hold opened"); setReason(""); onChanged();
    } catch (err) { toast.error(detailMessage(err)); }
  };
  const resolve = async (hold) => {
    const outcome = window.prompt("Review outcome (20 characters or more)");
    if (!outcome) return;
    const back = window.confirm("Return the driver to duty?");
    try {
      await api.post(`/driver-service/drivers/${profileId}/safety-holds/${hold.id}/resolve`, { reason: outcome, return_to_duty: back });
      toast.success("Safety hold resolved"); onChanged();
    } catch (err) { toast.error(detailMessage(err)); }
  };
  return (
    <div className="space-y-2 text-sm">
      {holds.map((h) => (
        <div key={h.id} className="rounded-lg border border-rose-200 bg-rose-50 p-2 flex flex-wrap justify-between gap-2">
          <span>{humanize(h.category)}: {h.reason}</span>
          <button onClick={() => resolve(h)} className="text-xs underline">Resolve after review</button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-2 py-1.5 rounded border">
          {HOLD_CATEGORIES.map((c) => <option key={c} value={c}>{humanize(c)}</option>)}
        </select>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="px-2 py-1.5 rounded border flex-1 min-w-0" />
        <button disabled={reason.length < 10} onClick={open} className="px-3 py-1.5 rounded border border-rose-300 text-rose-700 disabled:opacity-50">Open safety hold</button>
      </div>
    </div>
  );
}
